/**
 * Shared plumbing for visualize client tools that mutate the current
 * workspace through PubSubBus panel-mutation actions.
 *
 * The bus invokes subscribers synchronously in the publisher's stack (a
 * documented contract on {@link AiAgentThreadPubSubBus.publish}), so the
 * workspace's result callback has already fired by the time `publish()`
 * returns — {@link runPanelMutation} exploits that to give tools
 * request/response semantics over a fire-and-forget bus.
 */
import type { TopicService } from "@/shared/domain/topics/TopicService";
import { resolveSignalEntries } from "@/shared/domain/workspaces/resolveSignalEntries";
import type {
  PanelMutationActionPayload,
  WorkspaceActionEntry,
  WorkspaceActionResult,
} from "@/shared/services/AiAgentThreadPubSubBus/models";

import type { ClientToolExecutionResult } from "../ClientTools";
import { toolError, toolSuccess } from "../ClientTools";

/**
 * Upper bound on message paths per tool call. Keeps a single call from
 * flooding one panel (and the per-id record fetches) while comfortably
 * fitting real groupings like a 6-axis IMU.
 */
export const MAX_MESSAGE_PATHS_PER_CALL = 20;

/** True when `value` is an array of strings (the shape of id-list inputs). */
export function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

export interface FetchedEntries {
  entries: WorkspaceActionEntry[];
  /**
   * Ids whose records could not be loaded, with per-id reasons, in the
   * snake_case shape tools surface as `invalid_paths`. Why every failing id
   * is reported (not just the first) is documented on
   * {@link resolveSignalEntries}.
   */
  invalid: { message_path_id: string; reason: string }[];
}

/**
 * Fetch the message path + topic record for every id, never throwing for
 * per-id failures — they come back in `invalid` so the caller can fail
 * atomically with per-path reasons. Delegates to
 * {@link resolveSignalEntries}, renaming its camelCase failure key to the
 * snake_case shape of {@link FetchedEntries}.
 */
export async function fetchEntries(
  topicService: TopicService,
  messagePathIds: string[],
): Promise<FetchedEntries> {
  const { entries, invalid } = await resolveSignalEntries(
    topicService,
    messagePathIds,
  );
  return {
    entries,
    invalid: invalid.map(({ messagePathId, reason }) => ({
      message_path_id: messagePathId,
      reason,
    })),
  };
}

/**
 * Publish a panel-mutation workspace action and translate its synchronous
 * result into a client tool result.
 *
 * @param action - Action name echoed in the success response.
 * @param successExtra - Extra fields merged into the success response.
 */
export function runPanelMutation(
  publishWorkspaceAction: (payload: PanelMutationActionPayload) => boolean,
  action: string,
  buildPayload: (
    onResult: (result: WorkspaceActionResult) => void,
  ) => PanelMutationActionPayload,
  successExtra: Record<string, unknown> = {},
): ClientToolExecutionResult {
  let result: WorkspaceActionResult | undefined;

  const published = publishWorkspaceAction(
    buildPayload((r) => {
      result = r;
    }),
  );

  if (!published) {
    return toolError(
      "The visualizer is not ready to receive workspace actions yet. " +
        "Wait for the visualizer page to finish loading, then try again.",
    );
  }
  if (result === undefined) {
    return toolError("The visualizer did not report a result for this action.");
  }
  if (!result.ok) {
    return toolError(
      result.error,
      result.invalidPaths
        ? {
            invalid_paths: result.invalidPaths.map((path) => ({
              message_path_id: path.messagePathId,
              reason: path.reason,
            })),
          }
        : {},
    );
  }

  return toolSuccess(action, { panel_id: result.panelId, ...successExtra });
}
